/**
 * Poster Service
 * Manages poster data loading and caching
 */

import { apiClient } from './api-client.js';
import { saveTarget, getTarget } from '../utils/indexeddb.js';

/**
 * Poster Service class
 */
export class PosterService {
  constructor() {
    this.cache = new Map(); // In-memory cache for poster data
  }

  /**
   * Load poster data by ID
   * @param {string} posterId - Poster ID
   * @returns {Promise<object>} - Poster data
   */
  async loadPoster(posterId) {
    // Check in-memory cache first
    if (this.cache.has(posterId)) {
      console.log(`Poster ${posterId} loaded from memory cache`);
      return this.cache.get(posterId);
    }

    try {
      // Fetch from API
      const posterData = await apiClient.fetchPoster(posterId);

      // Cache in memory
      this.cache.set(posterId, posterData);

      console.log(`Poster ${posterId} loaded from API`);
      return posterData;
    } catch (error) {
      console.error(`Failed to load poster ${posterId}:`, error);
      throw error;
    }
  }

  /**
   * Download and cache target image for MindAR
   * @param {string} posterId - Poster ID
   * @param {string} targetImageUrl - URL to target image
   * @returns {Promise<ArrayBuffer>} - Target image data
   */
  async downloadTargetImage(posterId, targetImageUrl) {
    try {
      // Check IndexedDB cache first
      const cached = await getTarget(posterId);
      if (cached && cached.data) {
        console.log(`Target image for poster ${posterId} loaded from IndexedDB`);
        return cached.data;
      }

      // Download target image
      console.log(`Downloading target image for poster ${posterId}...`);
      const response = await fetch(targetImageUrl);

      if (!response.ok) {
        throw new Error(`Failed to download target image: ${response.status}`);
      }

      const targetData = await response.arrayBuffer();

      // Cache in IndexedDB
      await saveTarget(posterId, targetData);

      console.log(`Target image for poster ${posterId} downloaded and cached`);
      return targetData;
    } catch (error) {
      console.error(`Failed to download target image for poster ${posterId}:`, error);
      throw error;
    }
  }

  /**
   * Preload poster and target image
   * @param {string} posterId - Poster ID
   * @returns {Promise<object>} - Poster data with cached target
   */
  async preloadPoster(posterId) {
    const posterData = await this.loadPoster(posterId);

    // Download and cache target .mind file (use targetUrl from static config)
    if (posterData.targetUrl) {
      await this.downloadTargetImage(posterId, posterData.targetUrl);
    }

    return posterData;
  }

  /**
   * Clear poster cache
   * @param {string} posterId - Optional poster ID to clear specific poster
   */
  clearCache(posterId = null) {
    if (posterId) {
      this.cache.delete(posterId);
      console.log(`Poster ${posterId} cache cleared`);
    } else {
      this.cache.clear();
      console.log('All poster cache cleared');
    }
  }

  /**
   * Get cached poster data (without fetching)
   * @param {string} posterId - Poster ID
   * @returns {object|null} - Cached poster data or null
   */
  getCached(posterId) {
    return this.cache.get(posterId) || null;
  }
}

/**
 * Default poster service instance
 */
export const posterService = new PosterService();
